import { action, makeObservable, observable } from 'mobx';
import { appStore } from '../appStore';
import {
  IAllBranchesModalParams,
  IBranchLocationModalParams,
  IBranchesModalParams,
  ICategoryAndProductModal,
  ICategoryItemsModalParams,
  ICategoryMenuGroupsParams,
  ICrossSellingModal,
  ICustomerWalletModal,
  ICustomersModalParams,
  IDeliveryRequestInfoModalParams,
  IDeliveryTrackingModalParams,
  IDeliveryTypeParams,
  IDineInSectionModalParams,
  IItemsForDealModal,
  IMenuGroupItemModalParams,
  IModifierAndExclusionModal,
  IWeekModalParams,
  IWorkingHoursModalParams,
  IPauseDeliveryModal,
  IToYouMatchBranchesModal,
  ITimeConfirmModal,
  IT360SyncErrorsModal,
  IT360BranchesMappingModal,
  IT360MappedBranch,
  INoticeModal,
  INutrientsModal,
} from './types';
import { BRANCH_DELIVERY_TYPE, DELIVERY_TYPE } from 'src/constants/deliveryTypes';
import { delay, stringTransform } from 'src/utils/helpers';
import { arrayMerge } from '~/utils/formatter';
import { IAskAgainModal } from '~/view/containers/Modals/AskAgainModal/AskAgainModal';
import { IErrorModal, IErrorModalProps } from '~/view/containers/Modals/ErrorModal';
import { IBranchesLog } from '~/view/screens/BranchLogScreen/screens/BranchesLogScreen/store/branchesLogStore';
import { IMealChoiceEditModal } from '~/view/containers/Modals/MealChoiceEditModal';
import { IMealGroupEditModal } from '~/view/containers/Modals/MealGroupEditModal';
import { IMealItemEditModal } from '~/view/containers/Modals/MealItemEditModal';
import { IMealItemMdfExclModal } from '~/view/containers/Modals/MealItemMdfExclModal';
import { IMealSizeEditModal } from '~/view/containers/Modals/MealSizeEditModal';
import { IOrderProductModal } from '~/view/containers/Modals/OrderProductModal';
import { IPreviousOrderModal } from '~/view/containers/Modals/PreviousOrderModal';
import { IWHLog } from '~/view/screens/WorkingHoursLogScreen/screens/WorkingHoursLog/store/WorkingHoursLogStore';
import { IProductV4Modifier } from '~/types/product';
import { floatNumbersValidation } from '~/utils/validation';
import { nutrientsStore } from '~/view/containers/Modals/NutrientsModal/NutrientsStore';

export class ModalStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return false;
  }

  @observable errorModal: IErrorModalProps[] = [];
  openErrorModal = (errorModal: IErrorModal) => {
    this.errorModal = arrayMerge(this.errorModal, [{ id: errorModal.id || `${Date.now()}`, ...errorModal }], 'id');
  };
  closeErrorModal = (id: string) => {
    this.errorModal = this.errorModal.filter((errorModal) => errorModal.id !== id);
  };

  @observable customersModal: ICustomersModalParams | null = null;
  openCustomersModal = (customersModal: ICustomersModalParams) => {
    this.customersModal = customersModal;
  };
  closeCustomersModal = () => {
    this.customersModal = null;
  };

  @observable categoryAndProductModal: ICategoryAndProductModal | null = null;
  openCategoryAndProductModal = (categoryAndProductModal: ICategoryAndProductModal) => {
    this.categoryAndProductModal = categoryAndProductModal;
  };
  closeCategoryAndProductModal = () => {
    this.categoryAndProductModal = null;
  };

  @observable modifierAndExclusionModal: IModifierAndExclusionModal | null = null;
  openModifierAndExclusionModal = (modifierAndExclusionModal: IModifierAndExclusionModal) => {
    this.modifierAndExclusionModal = modifierAndExclusionModal;
  };
  closeModifierAndExclusionModal = () => {
    this.modifierAndExclusionModal = null;
  };

  @observable itemsForDealModal: IItemsForDealModal | null = null;
  openItemsForDealModal = (itemsForDealModal: IItemsForDealModal) => {
    this.itemsForDealModal = itemsForDealModal;
  };
  closeItemsForDealModal = () => {
    this.itemsForDealModal = null;
  };

  @observable previousOrderModal: IPreviousOrderModal | null = null;
  openPreviousOrderModal = (previousOrderModal: IPreviousOrderModal) => {
    this.previousOrderModal = previousOrderModal;
  };
  closePreviousOrderModal = () => {
    this.previousOrderModal = null;
  };

  @observable orderProduct: IOrderProductModal | null = null;
  openOrderProduct = (orderProduct: IOrderProductModal) => {
    this.orderProduct = orderProduct;
  };
  closeOrderProduct = () => {
    this.orderProduct = null;
  };
  reopenOrderProduct = async (orderProduct: IOrderProductModal) => {
    this.closeOrderProduct();
    await delay(100);
    this.openOrderProduct(orderProduct);
  };

  @observable deliveryType: IDeliveryTypeParams | null = null;
  openDeliveryType = (deliveryTypeParams: IDeliveryTypeParams) => {
    // by default editable
    if (deliveryTypeParams.editable === undefined) deliveryTypeParams.editable = true;

    if (appStore.settings?.delivery_type === BRANCH_DELIVERY_TYPE.BOTH) {
      deliveryTypeParams.delivery_types = [DELIVERY_TYPE.PICKUP, DELIVERY_TYPE.DELIVERY, DELIVERY_TYPE.DINE_IN];
      if (
        (deliveryTypeParams.delivery_type && !deliveryTypeParams.delivery_types.includes(deliveryTypeParams.delivery_type)) ||
        !deliveryTypeParams.delivery_type
      ) {
        deliveryTypeParams.delivery_type = DELIVERY_TYPE.DINE_IN;
      }
    } else if (appStore.settings?.delivery_type === BRANCH_DELIVERY_TYPE.PICKUP) {
      deliveryTypeParams.delivery_types = [DELIVERY_TYPE.PICKUP];
      deliveryTypeParams.delivery_type = DELIVERY_TYPE.PICKUP;
    } else if (appStore.settings?.delivery_type === BRANCH_DELIVERY_TYPE.DELIVERY) {
      deliveryTypeParams.delivery_types = [DELIVERY_TYPE.DELIVERY];
      deliveryTypeParams.delivery_type = DELIVERY_TYPE.DELIVERY;
    }

    this.deliveryType = deliveryTypeParams;
  };
  closeDeliveryType = () => {
    this.deliveryType = null;
  };

  @observable allBranchesModal: IAllBranchesModalParams | null = null;
  openAllBranchesModal = (allBranchesModal: IAllBranchesModalParams) => {
    this.allBranchesModal = allBranchesModal;
  };
  closeAllBranchesModal = () => {
    this.allBranchesModal = null;
  };

  @observable branchesModal: IBranchesModalParams | null = null;
  openBranchesModal = (branchesModal: IBranchesModalParams) => {
    this.branchesModal = branchesModal;
  };
  closeBranchesModal = () => {
    this.branchesModal = null;
  };

  @observable weekModal: IWeekModalParams | null = null;
  openWeekModal = (weekModal: IWeekModalParams) => {
    this.weekModal = weekModal;
  };
  closeWeekModal = () => {
    this.weekModal = null;
  };

  @observable categoryItemsModal: ICategoryItemsModalParams | null = null;
  openCategoryItemsModal = (categoryItemsModal: ICategoryItemsModalParams) => {
    this.categoryItemsModal = categoryItemsModal;
  };
  closeCategoryItemsModal = () => {
    this.categoryItemsModal = null;
  };

  @observable categoryMenuGroupsModal: ICategoryMenuGroupsParams | null = null;
  openCategoryMenuGroupsModal = (categoryMenuGroupsModal: ICategoryMenuGroupsParams) => {
    this.categoryMenuGroupsModal = categoryMenuGroupsModal;
  };
  closeCategoryMenuGroupsModal = () => {
    this.categoryMenuGroupsModal = null;
  };

  @observable crossSellingModal: ICrossSellingModal | null = null;
  openCrossSellingModal = (crossSellingModal: ICrossSellingModal) => {
    this.crossSellingModal = crossSellingModal;
  };
  closeCrossSellingModal = () => {
    this.crossSellingModal = null;
  };

  @observable deliveryTrackingModal: IDeliveryTrackingModalParams | null = null;
  openDeliveryTrackingModal = (deliveryTrackingModal: IDeliveryTrackingModalParams) => {
    this.deliveryTrackingModal = deliveryTrackingModal;
  };
  closeDeliveryTrackingModal = () => {
    this.deliveryTrackingModal = null;
  };

  @observable deliveryRequestInfoModal: IDeliveryRequestInfoModalParams | null = null;
  openDeliveryRequestInfoModal = (deliveryRequestInfoModal: IDeliveryRequestInfoModalParams) => {
    this.deliveryRequestInfoModal = deliveryRequestInfoModal;
  };
  closeDeliveryRequestInfoModal = () => {
    this.deliveryRequestInfoModal = null;
  };

  @observable menuGroupItemModal: IMenuGroupItemModalParams | null = null;
  @observable modifiers: IProductV4Modifier[] | null = null;
  openMenuGroupItemModal = (menuGroupItemModal: IMenuGroupItemModalParams) => {
    this.menuGroupItemModal = menuGroupItemModal;
    this.modifiers = menuGroupItemModal.item.modifiers;
  };
  closeMenuGroupItemModal = () => {
    this.menuGroupItemModal = null;
    this.modifiers = null;
  };

  onModifierPriceChange = (value: string, id?: number) => {
    if (this.modifiers?.length) {
      const price = stringTransform(value);
      this.modifiers = this.modifiers.map((modifier) => {
        return {
          ...modifier,
          options: modifier.options.map((option) => {
            if (option.id === id && floatNumbersValidation(price)) {
              return {
                ...option,
                menu_group_price: price,
              };
            }
            return option;
          }),
        };
      });
    }
  };

  /**
   * meals modals
   */
  @observable mealSizeEditModal: IMealSizeEditModal | null = null;
  openMealSizeEditModal = (mealSizeEditModal: IMealSizeEditModal) => {
    this.mealSizeEditModal = mealSizeEditModal;
  };
  closeMealSizeEditModal = () => {
    this.mealSizeEditModal = null;
  };

  @observable mealGroupEditModal: IMealGroupEditModal | null = null;
  openMealGroupEditModal = (mealGroupEditModal: IMealGroupEditModal) => {
    this.mealGroupEditModal = mealGroupEditModal;
  };
  closeMealGroupEditModal = () => {
    this.mealGroupEditModal = null;
  };

  @observable mealChoiceEditModal: IMealChoiceEditModal | null = null;
  openMealChoiceEditModal = (mealChoiceEditModal: IMealChoiceEditModal) => {
    this.mealChoiceEditModal = mealChoiceEditModal;
  };
  closeMealChoiceEditModal = () => {
    this.mealChoiceEditModal = null;
  };

  @observable mealItemEditModal: IMealItemEditModal | null = null;
  openMealItemEditModal = (mealItemEditModal: IMealItemEditModal) => {
    this.mealItemEditModal = mealItemEditModal;
  };
  closeMealItemEditModal = () => {
    this.mealItemEditModal = null;
  };

  @observable mealItemMdfExclModal: IMealItemMdfExclModal | null = null;
  openMealItemMdfExclModal = (mealItemMdfExclModal: IMealItemMdfExclModal) => {
    this.mealItemMdfExclModal = mealItemMdfExclModal;
  };
  closeMealItemMdfExclModal = () => {
    this.mealItemMdfExclModal = null;
  };
  /**
   * meals modals end
   */

  @observable branchLocationModal: IBranchLocationModalParams | null = null;
  openBranchLocationModal = (params: IBranchLocationModalParams) => {
    this.branchLocationModal = params;
  };
  closeBranchLocationModal = () => {
    this.branchLocationModal = null;
  };

  @observable dineInSectionModal: IDineInSectionModalParams | null = null;
  openDineInSectionModal = (params: IDineInSectionModalParams) => {
    this.dineInSectionModal = params;
  };
  closeDineInSectionModal = () => {
    this.dineInSectionModal = null;
  };

  @observable workingHoursModal: IWorkingHoursModalParams | null = null;
  openWorkingHoursModal = (params: IWorkingHoursModalParams) => {
    this.workingHoursModal = params;
  };
  closeWorkingHoursModal = () => {
    this.workingHoursModal = null;
  };

  @observable customerWalletModal: ICustomerWalletModal | null = null;
  openCustomerWalletModal = (params: ICustomerWalletModal) => {
    this.customerWalletModal = params;
  };
  closeCustomerWalletModal = () => {
    this.customerWalletModal = null;
  };

  @observable askAgainModal: IAskAgainModal | null = null;
  openAskAgainModal = (params: IAskAgainModal) => {
    this.askAgainModal = params;
  };
  closeAskAgainModal = () => {
    this.askAgainModal = null;
  };

  @observable updateModal: boolean | null = null;
  openUpdateModal = () => {
    this.updateModal = true;
  };
  closeUpdateModal = () => {
    this.updateModal = null;
  };

  @observable branchesLogModal: IBranchesLog | null = null;
  openBranchesLogModal = (params: IBranchesLog) => {
    this.branchesLogModal = params;
  };
  closeBranchesLogModal = () => {
    this.branchesLogModal = null;
  };

  @observable workingHoursLogModal: IWHLog | null = null;
  openWorkingHoursLogModal = (params: IWHLog) => {
    this.workingHoursLogModal = params;
  };
  closeWorkingHoursLogModal = () => {
    this.workingHoursLogModal = null;
  };

  @observable pauseDeliveryModal: IPauseDeliveryModal | null = null;
  @action openPauseDeliveryModal = (params: IPauseDeliveryModal) => {
    this.pauseDeliveryModal = params;
  };
  @action closePauseDeliveryModal = () => {
    this.pauseDeliveryModal = null;
  };

  @observable timeConfirmModal: ITimeConfirmModal | null = null;
  openTimeConfirmModal = (params: ITimeConfirmModal) => {
    this.timeConfirmModal = params;
  };
  closeTimeConfirmModal = () => {
    this.timeConfirmModal = null;
  };
  @observable nutrientsModal: INutrientsModal | null = null;
  @action openNutrientsModal = (params: INutrientsModal) => {
    this.nutrientsModal = params;
  };
  @action closeNutrientsModal = () => {
    this.nutrientsModal = null;
    nutrientsStore.clearTemp();
  };

  // Modals for ToYou integration
  @observable toYouMatchBranchesModal: IToYouMatchBranchesModal | null = null;
  @action openToYouMatchBranchesModal = (params: IToYouMatchBranchesModal) => {
    this.toYouMatchBranchesModal = params;
  };
  @action closeToYouMatchBranchesModal = () => {
    this.toYouMatchBranchesModal = null;
  };

  @observable t360SyncErrorsModal: IT360SyncErrorsModal | null = null;
  @action openT360SyncErrorsModal = (params: IT360SyncErrorsModal) => {
    this.t360SyncErrorsModal = params;
  };
  @action closeT360SyncErrorsModal = () => {
    this.t360SyncErrorsModal = null;
  };

  @observable t360BranchesMappingModal: IT360BranchesMappingModal | null = null;
  @action openT360BranchesMappingModal = (params: IT360BranchesMappingModal) => {
    this.t360BranchesMappingModal = params;
  };
  @action changeT360BranchesMappingModalMapping = (mappedBranches: IT360MappedBranch[]) => {
    this.t360BranchesMappingModal = {
      ...this.t360BranchesMappingModal,
      mappedBranches,
    } as IT360BranchesMappingModal;
  };

  @action changeT360BranchesMappingModalDuplicates = (duplicates: Set<string | number>) => {
    this.t360BranchesMappingModal = {
      ...this.t360BranchesMappingModal,
      duplicates,
    } as IT360BranchesMappingModal;
  };

  @action closeT360BranchesMappingModal = () => {
    this.t360BranchesMappingModal = null;
  };

  @observable noticeModal: INoticeModal | null = null;
  @action openNoticeModal = (params: INoticeModal) => {
    this.noticeModal = params;
  };
  @action closeNoticeModal = () => {
    this.noticeModal = null;
  };
}

export const modalStore = new ModalStore();
